import { Label } from "layout/typography/Label";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { useEffect, useState } from "react";
import {
  IProcedureCopayListItem,
  IProcedureCopayListItemSaveRequest,
} from "types/ProcedureCopayList/ProcedureCopayList";

import {
  ADD_ON_LABEL,
  ICopayListDraft,
  ITEM_NAME_HELP,
  copayListDraftProblem,
  copayListToSaveRequest,
  toCopayListDraft,
  unknownItemWarning,
} from "./copayListWording";

interface ICopayListDialogProps {
  visible: boolean;
  /** The row being edited, or null when adding. */
  row: IProcedureCopayListItem | null;
  /** Every row on the list, for the duplicate check. */
  rows: IProcedureCopayListItem[];
  /** Every item name in use — the suggestions, and what the warning checks against. */
  itemNames: string[];
  saving: boolean;
  onSave: (body: IProcedureCopayListItemSaveRequest) => void;
  onHide: () => void;
}

/**
 * CLE-2816 PR 2 — Add / Edit one code of the copay list. A draft held locally, checked here with
 * the API's own wording before it is sent, and the whole row sent on save. The item box suggests
 * the names already in use and WARNS (never blocks) when the typed name is not one of them: the
 * engine compares it byte for byte with the payer's line, so a near miss matches nothing.
 */
export function CopayListDialog({
  visible,
  row,
  rows,
  itemNames,
  saving,
  onSave,
  onHide,
}: ICopayListDialogProps) {
  const [draft, setDraft] = useState<ICopayListDraft>(toCopayListDraft(row));
  const [problem, setProblem] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    if (visible) {
      setDraft(toCopayListDraft(row));
      setProblem(null);
    }
  }, [visible, row]);

  const set = (patch: Partial<ICopayListDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const suggest = (query: string) => {
    const typed = (query ?? "").trim().toUpperCase();
    setSuggestions(
      typed
        ? itemNames.filter((name) => name.toUpperCase().includes(typed))
        : [...itemNames]
    );
  };

  const warning = unknownItemWarning(draft.itemName, draft.addOn, itemNames);

  const save = () => {
    const found = copayListDraftProblem(draft, rows, row?.cptCode ?? null);
    setProblem(found);
    if (found) return;
    onSave(copayListToSaveRequest(draft));
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        type="button"
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        data-testid="copayList.dialog.cancel"
      />
      <Button
        type="button"
        label={row ? "Save code" : "Add code"}
        loading={saving}
        onClick={save}
        data-testid="copayList.dialog.save"
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={row ? `Edit ${row.cptCode}` : "Add a code to the copay list"}
      footer={footer}
      style={{ width: "min(600px, 95vw)" }}
      data-testid="copayList.dialog"
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-column gap-1">
          <Label htmlFor="copayList.cptCode">CPT / HCPCS code</Label>
          <InputText
            id="copayList.cptCode"
            data-testid="copayList.cptCode"
            value={draft.cptCode}
            onChange={(event) => set({ cptCode: event.target.value })}
            placeholder="69436"
          />
          <small className="text-color-secondary">
            Five digits, or a HCPCS letter and four digits, without a modifier.
          </small>
        </div>

        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="copayList.addOn"
            data-testid="copayList.addOn"
            checked={draft.addOn}
            onChange={(event) => set({ addOn: !!event.checked })}
          />
          <label htmlFor="copayList.addOn">{ADD_ON_LABEL}</label>
        </div>

        {!draft.addOn && (
          <div className="flex flex-column gap-1">
            <Label htmlFor="copayList.itemName">Item on the payer’s list</Label>
            <AutoComplete
              inputId="copayList.itemName"
              data-testid="copayList.itemName"
              value={draft.itemName}
              suggestions={suggestions}
              completeMethod={(event) => suggest(event.query)}
              onChange={(event) => set({ itemName: event.value ?? "" })}
              dropdown
              placeholder="EAR TUBES"
              style={{ width: "100%" }}
              inputStyle={{ width: "100%" }}
            />
            <small className="text-color-secondary">{ITEM_NAME_HELP}</small>
            {warning && (
              <Message
                severity="warn"
                text={warning}
                data-testid="copayList.dialog.unknownItem"
              />
            )}
          </div>
        )}
        {draft.addOn && (
          <small
            className="text-color-secondary"
            data-testid="copayList.dialog.addOnHelp"
          >
            An add-on (an implant or device, a bone graft, neuromonitoring,
            robotic assistance, an extra level) never picks the item; the
            estimate’s main procedure does.
          </small>
        )}

        <div className="flex flex-column gap-1">
          <Label htmlFor="copayList.note">Note (optional)</Label>
          <InputText
            id="copayList.note"
            data-testid="copayList.note"
            value={draft.note}
            onChange={(event) => set({ note: event.target.value })}
            placeholder="Create eardrum opening — matched on payments"
          />
          <small className="text-color-secondary">
            One line about the code, never about a patient.
          </small>
        </div>

        {problem && (
          <Message
            severity="error"
            text={problem}
            data-testid="copayList.dialog.problem"
          />
        )}
      </div>
    </Dialog>
  );
}
